import React, { useState, useMemo } from 'react';
import { Search, ChevronUp, ChevronDown, Download } from 'lucide-react';

/**
 * Interactive data table component with sorting, filtering, and search.
 * Designed to work with standardized event data from maintenance handlers.
 */
const DataTable = ({ data, onExport }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 25;

    // Extract columns from data
    const columns = useMemo(() => {
        if (!data || data.length === 0) return [];

        // Priority columns to show first
        const priorityKeys = ['formatted_date', 'calUnitNo', 'Unit', 'description', 'work_order', 'shift', 'comments', 'Planned?'];
        const allKeys = Object.keys(data[0]);

        // Filter and order columns
        const cols = [];
        priorityKeys.forEach(key => {
            if (allKeys.includes(key)) cols.push(key);
        });

        // Add remaining columns (excluding internal/complex fields)
        const excludeKeys = [
            'display_desc', 'timestamp', 'parsed_date',
            'd8date', 'D8 Date', 'D8 DATE',
            'down date and time', 'DOWN DATE AND TIME',
            'Issue Code', 'ISSUE CODE', 'issue_code', 'issuecode',
            ...cols
        ];
        const excludeKeysLower = excludeKeys.map(k => String(k).toLowerCase());
        allKeys.forEach(key => {
            const keyLower = String(key).toLowerCase();
            const keyClean = keyLower.replace(/[_\n]/g, ' ').replace(/\s+/g, ' ').trim();
            if (
                !excludeKeys.includes(key) &&
                !excludeKeysLower.includes(keyLower) &&
                !excludeKeysLower.includes(keyClean) &&
                !key.startsWith('_')
            ) {
                cols.push(key);
            }
        });

        return cols;
    }, [data]);

    // Format column header for display
    const formatHeader = (key) => {
        return key
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
            .trim();
    };

    // Format cell value for display
    const formatCell = (value, key) => {
        if (value === null || value === undefined || value === 'N/A') return '-';

        // Handle timestamps
        if (key === 'timestamp' && value) {
            try {
                return new Date(value).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', hour12: true });
            } catch {
                return value;
            }
        }

        // Truncate long text
        const str = String(value);
        if (str.length > 100) {
            return str.substring(0, 100) + '...';
        }

        return str;
    };

    // Filter data based on search term
    const filteredData = useMemo(() => {
        if (!searchTerm || !data) return data;

        const lowerSearch = searchTerm.toLowerCase();
        return data.filter(row =>
            Object.values(row).some(val =>
                String(val).toLowerCase().includes(lowerSearch)
            )
        );
    }, [data, searchTerm]);

    // Sort data
    const sortedData = useMemo(() => {
        if (!sortConfig.key || !filteredData) return filteredData;

        const sorted = [...filteredData].sort((a, b) => {
            const aVal = a[sortConfig.key];
            const bVal = b[sortConfig.key];

            // Handle null/undefined
            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            // Numeric comparison
            if (typeof aVal === 'number' && typeof bVal === 'number') {
                return aVal - bVal;
            }

            // String comparison
            return String(aVal).localeCompare(String(bVal));
        });

        return sortConfig.direction === 'desc' ? sorted.reverse() : sorted;
    }, [filteredData, sortConfig]);

    // Pagination
    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedData?.slice(startIndex, startIndex + itemsPerPage) || [];
    }, [sortedData, currentPage]);

    const totalPages = Math.ceil((sortedData?.length || 0) / itemsPerPage);

    // Handle sort
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    if (!data || data.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                No data to display
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Search and Controls */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div className="relative w-full sm:flex-1 sm:max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search all columns..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                </div>

                {onExport && (
                    <button
                        onClick={onExport}
                        className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Export Excel
                    </button>
                )}
            </div>

            {/* Stats */}
            <div className="text-sm text-gray-600">
                Showing {paginatedData.length} of {sortedData?.length || 0} events
                {searchTerm && ` (filtered from ${data.length} total)`}
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[600px] overflow-y-auto custom-scrollbar">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map(col => (
                                <th
                                    key={col}
                                    onClick={() => handleSort(col)}
                                    className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        {formatHeader(col)}
                                        {sortConfig.key === col && (
                                            sortConfig.direction === 'asc'
                                                ? <ChevronUp className="w-4 h-4" />
                                                : <ChevronDown className="w-4 h-4" />
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {paginatedData.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                {columns.map(col => (
                                    <td key={col} className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                                        {formatCell(row[col], col)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Pagination — responsive: wrap on small screens, touch-friendly buttons */}
            {totalPages > 1 && (
                <div className="flex flex-wrap items-center justify-center sm:justify-between gap-3 py-2">
                    <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                        Previous
                    </button>
                    <span className="text-xs sm:text-sm text-gray-600 order-first w-full sm:order-none sm:w-auto text-center sm:text-left">
                        Page {currentPage} of {totalPages}
                    </span>
                    <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="min-h-[44px] min-w-[44px] px-3 sm:px-4 py-2 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default DataTable;
